import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Container } from './Container'
import type { UpboTag } from '@/types'

interface SectionProps extends HTMLAttributes<HTMLElement>, UpboTag {
  /** Letterspaced caps above the title — the house label voice. */
  eyebrow?: string
  title?: string
  subtitle?: string
  /** Trailing content beside the title ("View all" links, filters…). */
  action?: ReactNode
  containerSize?: 'sm' | 'md' | 'lg' | 'xl'
  /** Centre the header block instead of aligning it left. */
  centered?: boolean
  bleed?: boolean
}

/**
 * One band of content with a heading. Reused on every page so the vertical
 * rhythm of the site stays identical from the home page to the studio.
 */
export function Section({
  eyebrow,
  title,
  subtitle,
  action,
  children,
  className,
  containerSize = 'lg',
  centered = false,
  bleed = false,
  ...props
}: SectionProps) {
  const header = (eyebrow || title || subtitle || action) && (
    <div
      className={cn(
        'mb-8 sm:mb-12 flex flex-wrap gap-4',
        centered ? 'flex-col items-center text-center' : 'items-end justify-between',
      )}
    >
      <div className={cn('flex flex-col gap-3', centered && 'items-center')}>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        {title && <h2 className="text-[32px] sm:text-[42px] leading-[1.08]">{title}</h2>}
        {subtitle && (
          <p className={cn('text-[15px] text-text-muted max-w-xl', centered && 'mx-auto')}>{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  )

  return (
    <section data-upbo-type="section" className={cn('py-16 sm:py-24', className)} {...props}>
      {bleed ? (
        <>
          {header}
          {children}
        </>
      ) : (
        <Container size={containerSize}>
          {header}
          {children}
        </Container>
      )}
    </section>
  )
}
